import type { MetricsRow } from 'app/services/metrics'
import { useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { useEffect } from 'react'
import ProgressBar from 'app/components/ProgressBar'
import { CallsModal } from 'app/components/scorecards/CallsModal'
import MonthlyLocationBreakdownTable from 'app/components/shared/MonthlyLocationBreakdownTable'
import MonthlyStaffBreakdownTable from 'app/components/shared/MonthlyStaffBreakdownTable'
import { Stats } from 'app/components/Stats'
import MetricCell from 'app/components/table/MetricCell'
import { PMSIntegrationCell } from 'app/components/table/PMSIntegrationCell'
import TagsCell from 'app/components/tags/TagsCell'
import { formatDate } from 'app/date.utils'
import { PredefinedFilter } from 'app/filter.utils'
import type { ActivityJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { staffMetricColumns } from 'app/table-columns/shared/staff-metric-columns'
import { divide } from 'app/utils'
import { getActivityTypeColorMap } from 'lib/colorMappings'
import { showSpend } from 'lib/showSpend'

const leadStatuses =
  [`Needs Action`, `In Progress`, `Closed Won`, `Closed Lost`]?.map((status) => {
    const infoTooltipMessage = (() => {
      switch (status) {
        case `Needs Action`:
          return `All contacts who have a lead status of “Needs Action” and a became a lead date within the date range. A needs action describes a contact who requires further action from the office administrative team.`
        case `In Progress`:
          return `All contacts who have a lead status of “In Progress” and a became a lead date within the date range. An in progress lead status describes a contact where the office administrative team is actively taking action to schedule.`
        case `Closed Won`:
          return `All contacts who have a lead status of “Closed Won” and a became a lead date within the date range. A closed won lead status describes a contact who has completed a first appointment.`
        case `Closed Lost`:
          return `All contacts who have a lead status of “Closed Lost” and a became a lead date within the date range. A closed lost lead status describes a contact who is disqualified or could not be scheduled for an appointment.`
        default:
          return `Missing info tooltip message`
      }
    })()
    return {
      accessorFn: (row) => row.leadStatus?.[status],
      enableSorting: true,
      cell: (props) => {
        return <>{props.row.original?.leadStatus?.[status] ?? 0}</>
      },
      header: `${status} Leads`,
      id: `${status} Leads`,
      meta: {
        group: `Lead Status`,
        defaultVisible: true,
        sortKey: `${status}`,
        infoTooltip: {
          title: status,
          message: infoTooltipMessage,
        },
      },
      enableHiding: true,
      footer: ({ table }) => (
        <MetricCell
          value={table
            .getFilteredRowModel()
            .rows.reduce((total, row) => total + (row.original?.leadStatus?.[status] ?? 0), 0)}
          type="unitless"
        />
      ),
    }
  }) ?? []

export const defaultLocationColumns = (
  showSensitiveColumns: boolean,
  isSMC: boolean,
  filters: string,
  canViewSpend: boolean,
  staffV2Enabled: boolean,
): ColumnDef<MetricsRow, any>[] => {
  // Combine permission check with filter-based logic
  // staff-v2 ON: require permission AND filter-based logic
  // staff-v2 OFF: only filter-based logic (legacy)
  const shouldShowSpend = staffV2Enabled ? canViewSpend && showSpend(isSMC, filters) : showSpend(isSMC, filters)

  return [
    {
      enableSorting: true,
      accessorFn: (row) => row.location?.name,
      header: `Location Name`,
      id: `Location Name`,
      meta: {
        group: `General`,
        defaultVisible: true,
        pinned: true,
        onClick: (row) =>
          showModal(
            <MonthlyLocationBreakdownTable locationIds={[row.locationId!]} columns={locationMonthlyColumns(isSMC)} />,
            {
              title: row.location?.name || ``,
              isFullScreen: false,
              is80Percent: true,
              useRadix: true,
            },
          ),
      },
      enableHiding: false,
      footer: `Total`,
    },
    ...(showSensitiveColumns
      ? [
          {
            accessorFn: (row) => row.location?.orgTags,
            id: `Org Tags`,
            header: `Org Tags`,
            meta: {
              group: `General`,
              defaultVisible: false,
              ControlledComponent: (row) => (
                <TagsCell tags={row?.location?.orgTags} model="locations" modelId={row?.locationId!} isOrgTags />
              ),
            },
            enableSorting: false,
          },
        ]
      : []),
    {
      accessorFn: (row) => row.location?.pmsIntegrated,
      id: `PMS Integration`,
      header: `PMS Integration`,
      cell: (props) => (
        <PMSIntegrationCell
          isIntegrated={props.getValue()}
          lastPmsSyncDate={props.row.original.location?.lastPmsSyncDate}
        />
      ),
      meta: {
        group: `General`,
        defaultVisible: false,
        infoTooltip: {
          title: `PMS Integration`,
          message: `Denotes whether there is a linked PMS in the Location Settings: Integrations.`,
        },
      },
      sortUndefined: -1,
    },
    {
      accessorFn: (row) => row.location?.tags,
      id: `Tags`,
      header: `Tags`,
      meta: {
        group: `General`,
        defaultVisible: true,
        ControlledComponent: (row) => (
          <TagsCell
            tags={row?.location?.tags}
            workspaceId={row?.location?.workspaceId}
            model="locations"
            modelId={row?.locationId!}
          />
        ),
      },
      enableSorting: false,
    },
    {
      accessorKey: `leads`,
      cell: (props) => <MetricCell value={props.row.original.leads} />,
      accessorFn: (row) => row.leads,
      header: `Leads`,
      id: `Leads`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
        infoTooltip: {
          title: `Leads`,
          message: `All contacts that have a became a lead date within the date range. This includes both qualified and disqualified leads.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leads, 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorKey: `MQLs`,
      cell: (props) => <MetricCell value={props.row.original.MQLs} />,
      accessorFn: (row) => row.MQLs,
      header: `Qualified Leads`,
      id: `Qualified Leads`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
        infoTooltip: {
          title: `Qualified Leads`,
          message: `All contacts with a became a lead date within the date range who does not have a disqualifying closed lost reason.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.MQLs, 0)}
          type="unitless"
        />
      ),
    },
    ...(isSMC
      ? [
          {
            accessorKey: `budget`,
            cell: (props) => <MetricCell value={props.row.original.budget} type="dollars" />,
            accessorFn: (row) => row.budget,
            header: `Budget`,
            id: `Budget`,
            meta: {
              group: `Financial`,
              defaultVisible: true,
              infoTooltip: {
                title: `Budget`,
                message: `The total marketing retainer or budget assigned to the channels within the date range.`,
              },
            },
            footer: ({ table }) => (
              <MetricCell
                value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0)}
                type="dollars"
              />
            ),
          },
          {
            accessorKey: `leadGoal`,
            cell: (props) => <MetricCell value={props.row.original.leadGoal} type="unitless" />,
            accessorFn: (row) => row.leadGoal,
            header: `Lead Goal`,
            id: `Lead Goal`,
            meta: {
              group: `General`,
              defaultVisible: false,
              infoTooltip: {
                title: `Lead Goal`,
                message: `Found in settings, the lead goal is the established lead target for the marketing retainer or budget.`,
              },
            },
            footer: ({ table }) => (
              <MetricCell
                value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadGoal, 0)}
                type="unitless"
              />
            ),
          },
          {
            accessorKey: `patientGoal`,
            cell: (props) => <MetricCell value={props.row.original.patientGoal} type="unitless" />,
            accessorFn: (row) => row.patientGoal,
            header: `New Patient Goal`,
            id: `New Patient Goal`,
            meta: {
              group: `General`,
              defaultVisible: true,
              infoTooltip: {
                title: `New Patient Goal`,
                message: `The new patient goal from the budget tab for this location.`,
              },
            },
            footer: ({ table }) => (
              <MetricCell
                value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.patientGoal, 0)}
                type="unitless"
              />
            ),
          },
        ]
      : [
          {
            accessorKey: `totalSpend`,
            cell: (props) =>
              shouldShowSpend ? <MetricCell value={props.row.original.totalSpend} type="dollars" /> : <>-</>,
            accessorFn: (row) => row.totalSpend,
            header: `Spend`,
            id: `Spend`,
            meta: {
              group: `Financial`,
              defaultVisible: true,
              infoTooltip: {
                title: `Spend`,
                message: `Total Facebook spend + Google spend within the date range.`,
              },
            },
            footer: ({ table }) =>
              shouldShowSpend ? (
                <MetricCell
                  value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.totalSpend, 0)}
                  type="dollars"
                />
              ) : (
                <>-</>
              ),
          },
        ]),
    {
      accessorFn: (row) => row.production + row.scheduledProduction,
      id: `Production`,
      header: `Net Production`,
      footer: ({ table }) => (
        <Stats
          title="Net Production"
          count={table
            .getFilteredRowModel()
            .rows.reduce((total, row) => total + row.original.production + row.original.scheduledProduction, 0)}
          unitType="dollars"
          breakdown={[
            {
              name: `Completed Production`,
              count: table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.production, 0),
            },
            {
              name: `Scheduled Production`,
              count: table
                .getFilteredRowModel()
                .rows.reduce((total, row) => total + row.original.scheduledProduction, 0),
            },
          ]}
        >
          <MetricCell
            value={table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + row.original.production + row.original.scheduledProduction, 0)}
            type="dollars"
          />
        </Stats>
      ),
      meta: {
        group: `Financial`,
        defaultVisible: true,
        infoTooltip: {
          title: `Net Production`,
          message: `The total production (completed and scheduled) after write offs and adjustments for contacts with a became a lead date within the date range.`,
        },
        ControlledComponent: (row) => (
          <Stats
            title="Net Production"
            count={row!.production + row!.scheduledProduction}
            unitType="dollars"
            breakdown={[
              { name: `Completed Production`, count: row!.production },
              { name: `Scheduled Production`, count: row!.scheduledProduction },
            ]}
          >
            <MetricCell value={row!.production + row!.scheduledProduction} type="dollars" />
          </Stats>
        ),
      },
    },
    {
      accessorKey: `ROI`,
      cell: (props) => <MetricCell value={props.row.original.ROI} type="percentage" />,
      accessorFn: (row) => row.ROI,
      header: `ROI`,
      id: `ROI`,
      meta: {
        group: `Financial`,
        defaultVisible: true,
        infoTooltip: {
          title: `ROI`,
          message: `Return on investment: net production divided by ${isSMC ? `budget` : `spend`}.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={
            (() => {
              const production: number = table
                .getFilteredRowModel()
                .rows.reduce((total, row) => total + row.original.production, 0)
              const scheduled: number = table
                .getFilteredRowModel()
                .rows.reduce((total, row) => total + row.original.scheduledProduction, 0)
              const budgetOrSpend: number = isSMC
                ? table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0)
                : table
                    .getFilteredRowModel()
                    .rows.reduce(
                      (total, row) => total + (row.original.googleSpend || 0) + (row.original.facebookSpend || 0),
                      0,
                    )

              return (production + scheduled - budgetOrSpend) / budgetOrSpend
            })() * 100
          }
          type="percentage"
        />
      ),
    },
    {
      accessorKey: `appointments`,
      cell: (props) => <MetricCell value={props.row.original.appointments} />,
      accessorFn: (row) => row.appointments,
      header: `Appointments Scheduled`,
      id: `Appointments Scheduled`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
        infoTooltip: {
          title: `Appointments Scheduled`,
          message: `All contacts who have a became a lead within the date range selected and have scheduled an appointment at any point. However this does not mean they have completed the appointment.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.appointments, 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorKey: `patients`,
      cell: (props) => <MetricCell value={props.row.original.patients} />,
      accessorFn: (row) => row.patients,
      header: `New Patients`,
      id: `New Patients`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
        infoTooltip: {
          title: `New Patients`,
          message: `All contacts who have have a became a lead date within the date range and have completed a first appointment.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.patients, 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorKey: `CPL`,
      cell: (props) => <MetricCell value={props.row.original.CPL} type="dollars" />,
      accessorFn: (row) => row.CPL,
      header: `CPL`,
      id: `CPL`,
      meta: {
        group: `Calculations`,
        defaultVisible: false,
        infoTooltip: {
          title: `CPL`,
          message: `Total ${
            isSMC ? `retainer or budget` : `spend`
          } divided by number of contacts with a became a lead date from the selected channels and within the date range.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={
            (isSMC
              ? table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0)
              : table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original.totalSpend ?? 0), 0)) /
            table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leads, 0)
          }
          type="dollars"
        />
      ),
    },
    {
      accessorKey: `CPQL`,
      cell: (props) => <MetricCell value={props.row.original.CPQL} type="dollars" />,
      accessorFn: (row) => row.CPQL,
      header: `CPQL`,
      id: `CPQL`,
      meta: {
        group: `Calculations`,
        defaultVisible: false,
        infoTooltip: {
          title: `CPQL`,
          message: `Total ${
            isSMC ? `retainer or budget` : `spend`
          } divided by number of qualified contacts with a became a lead date from the selected channels and within the date range.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={
            (isSMC
              ? table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0)
              : table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original.totalSpend ?? 0), 0)) /
            table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.MQLs, 0)
          }
          type="dollars"
        />
      ),
    },
    {
      accessorKey: `CPA`,
      cell: (props) => <MetricCell value={props.row.original.CPA} type="dollars" />,
      accessorFn: (row) => row.CPA,
      header: `CPA`,
      id: `CPA`,
      meta: {
        group: `Calculations`,
        defaultVisible: false,
        infoTooltip: {
          title: `CPA`,
          message: `Total ${
            isSMC ? `retainer or budget` : `spend`
          } divided by number of appointments generated from the selected channels and within the date range. This contains a count of contacts who have scheduled an appointment regardless of whether it has been completed or not.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={
            (isSMC
              ? table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0)
              : table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original.totalSpend ?? 0), 0)) /
            table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.appointments, 0)
          }
          type="dollars"
        />
      ),
    },
    {
      accessorKey: `CAC`,
      cell: (props) => <MetricCell value={props.row.original.CAC} type="dollars" />,
      accessorFn: (row) => row.CAC,
      header: `CAC`,
      id: `CAC`,
      meta: {
        group: `Calculations`,
        defaultVisible: false,
        infoTooltip: {
          title: `CAC`,
          message: `Total ${
            isSMC ? `retainer or budget` : `spend`
          } divided by contacts who completed their first appointment from the selected channels and within the date range.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={
            (isSMC
              ? table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0)
              : table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original.totalSpend ?? 0), 0)) /
            table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.patients, 0)
          }
          type="dollars"
        />
      ),
    },
    {
      accessorFn: (row) => row.scheduleRate,
      accessorKey: `scheduleRate`,
      header: `Scheduled Rate`,
      id: `Scheduled Rate`,
      meta: {
        group: `Calculations`,
        defaultVisible: true,
        infoTooltip: {
          title: `Scheduled Rate`,
          message: `Percentage of qualified leads that scheduled a first appointment within the date range.`,
        },
        ControlledComponent: (row) => (
          <div className="flex items-center gap-x-3 px-2">
            <ProgressBar progress={row!.scheduleRate} />
            <span className="text-body-xs">
              <MetricCell value={row!.scheduleRate} type="percentage" />
            </span>
          </div>
        ),
      },
      footer: ({ table }) => (
        <MetricCell
          value={
            (table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original?.appointments ?? 0), 0) /
              table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original?.qualified ?? 0), 0)) *
            100
          }
          type="percentage"
        />
      ),
    },
    {
      accessorKey: `leadsNeedsAction`,
      accessorFn: (row) => row.leadsNeedsAction,
      cell: (props) => <MetricCell value={props.getValue()} />,
      header: `Needs Action`,
      id: `Needs Action Leads`,
      meta: {
        group: `Lead Status`,
        defaultVisible: true,
        infoTooltip: {
          title: `Needs Action Leads`,
          message: `All contacts who have a lead status of “Needs Action” and a became a lead date within the date range. A needs action describes a contact who requires further action from the office administrative team.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsNeedsAction, 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorKey: `leadsInProgress`,
      accessorFn: (row) => row.leadsInProgress,
      cell: (props) => <MetricCell value={props.getValue()} />,
      header: `In Progress`,
      id: `In Progress Leads`,
      meta: {
        group: `Lead Status`,
        defaultVisible: true,
        infoTooltip: {
          title: `In Progress Leads`,
          message: `All contacts who have a lead status of “In Progress” and a became a lead date within the date range. An in progress lead status describes a contact where the office administrative team is actively taking action to schedule.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsInProgress, 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorKey: `leadsScheduled`,
      accessorFn: (row) => row.leadsScheduled,
      cell: (props) => <MetricCell value={props.getValue()} />,
      header: `Scheduled Leads`,
      id: `Scheduled Leads`,
      meta: {
        group: `Lead Status`,
        defaultVisible: true,
        infoTooltip: {
          title: `Scheduled Leads`,
          message: `All contacts who have a lead status of “Scheduled” and a became a lead date within the date range. A scheduled lead status describes a contact who has a scheduled first appointment in the future.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsScheduled, 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorKey: `leadsDisqualified`,
      accessorFn: (row) => row.leadsDisqualified,
      cell: (props) => <MetricCell value={props.getValue()} />,
      header: `Disqualified Leads`,
      id: `Disqualified Leads`,
      meta: {
        group: `Totals`,
        defaultVisible: false,
        infoTooltip: {
          title: `Disqualified Leads`,
          message: `All contacts with a became a lead date within the date range who has a disqualifying closed lost reason.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsDisqualified, 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorKey: `leadsClosedWon`,
      accessorFn: (row) => row.leadsClosedWon,
      cell: (props) => <MetricCell value={props.getValue()} />,
      header: `Closed Won`,
      id: `Closed Won Leads`,
      meta: {
        group: `Lead Status`,
        defaultVisible: false,
        infoTooltip: {
          title: `Closed Won Leads`,
          message: `All contacts who have a lead status of “Closed Won” and a became a lead date within the date range. A closed won lead status describes a contact who has completed a first appointment.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsClosedWon, 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorKey: `leadsClosedLost`,
      accessorFn: (row) => row.leadsClosedLost,
      cell: (props) => <MetricCell value={props.getValue()} />,
      header: `Closed Lost`,
      id: `Closed Lost Leads`,
      meta: {
        group: `Lead Status`,
        defaultVisible: true,
        infoTooltip: {
          title: `Closed Lost Leads`,
          message: `All contacts who have a lead status of “Closed Lost” and a became a lead date within the date range. A closed lost lead status describes a contact who is disqualified or could not be scheduled for an appointment.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsClosedLost, 0)}
          type="unitless"
        />
      ),
    },
    {
      accessorFn: (row) => row.leadsWithMissedCall,
      id: `Missed Calls`,
      header: `Missed Calls`,
      cell: (props) => (
        <Stats
          title="Missed Calls"
          unitType="unitless"
          count={props.getValue()}
          breakdown={[
            { name: `Missed Calls`, count: props.row.original.leadsWithMissedCall },
            { name: `Voicemail Calls`, count: props.row.original.leadsWithVoicemailCall },
          ]}
        >
          <MetricCell value={props.getValue()} type="unitless" />
        </Stats>
      ),
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsWithMissedCall, 0)}
          type="unitless"
        />
      ),
      meta: {
        group: `Totals`,
        defaultVisible: false,
      },
    },
  ]
}

function StaffName({ props, fromDate, toDate, channel }) {
  const [searchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get(`details`) === props.getValue()) {
      showModal(
        <MonthlyStaffBreakdownTable
          staffName={props.getValue()}
          title={props.getValue() || ``}
          fromDate={fromDate}
          toDate={toDate}
          channel={channel}
          onClose={closeModal}
        />,
        {
          hideTitle: true,
          removePadding: true,
          isFullScreen: true,
          useRadix: true,
        },
      )
    }
  }, [])

  return <span>{props.getValue()}</span>
}

export const getDefaultStaffColumns = (
  fromDate: string,
  toDate: string,
  channel: string,
  defaultFilters?: PredefinedFilter,
) => [
  {
    enableSorting: true,
    accessorFn: (row) => row.name,
    header: `Staff Name`,
    id: `Staff Name`,
    cell: (props) => <StaffName props={props} fromDate={fromDate} toDate={toDate} channel={channel} />,
    meta: {
      group: `Schedule Rate`,
      defaultVisible: true,
      pinned: true,
      sortKey: `leadOwner`,
      onClick: (row) => {
        showModal(
          <MonthlyStaffBreakdownTable
            title={row.name || ``}
            staffName={row.name}
            fromDate={fromDate}
            toDate={toDate}
            channel={channel}
            onClose={closeModal}
            defaultFilters={defaultFilters}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
          },
        )
      },
    },
    enableHiding: false,
    footer: `Total`,
  },
  {
    enableSorting: true,
    accessorFn: (row) => row.scheduled,
    header: `Scheduled`,
    id: `Scheduled`,
    meta: {
      group: `Schedule Rate`,
      defaultVisible: true,
      sortKey: `Scheduled`,
      infoTooltip: {
        title: `Scheduled`,
        message: `All qualified contacts with a became a lead date within the date range and have scheduled a first appointment.`,
      },
    },
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.scheduled, 0)}
        type="unitless"
      />
    ),
  },
  {
    enableSorting: true,
    accessorFn: (row) => row.notScheduled,
    header: `Not Scheduled`,
    id: `Not Scheduled`,
    meta: {
      group: `Schedule Rate`,
      defaultVisible: true,
      sortKey: `Not Scheduled`,
      infoTooltip: {
        title: `Not Scheduled`,
        message: `All qualified contacts with a became a lead date within the date range and have not scheduled a first appointment.`,
      },
    },
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.notScheduled, 0)}
        type="unitless"
      />
    ),
  },
  {
    enableSorting: true,
    accessorFn: (row) => row.leads,
    header: `Qualified Leads`,
    id: `Qualified Leads`,
    meta: {
      group: `Schedule Rate`,
      defaultVisible: true,
      sortKey: `leads`,
      infoTooltip: {
        title: `Qualified Leads`,
        message: `All contacts with a became a lead date within the date range who does not have a disqualifying closed lost reason.`,
      },
    },
    enableHiding: true,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leads, 0)}
        type="unitless"
      />
    ),
  },
  {
    enableSorting: true,
    accessorFn: (row) => (row.scheduled / row.leads) * 100,
    header: `Scheduled Rate`,
    id: `Scheduled Rate`,
    meta: {
      group: `Schedule Rate`,
      defaultVisible: true,
      sortKey: `scheduled`,
      infoTooltip: {
        title: `Scheduled`,
        message: `Percentage of qualified leads that scheduled a first appointment within the date range.`,
      },
      ControlledComponent: (row) => (
        <Stats
          title="Scheduled Rate"
          unitType="percentage"
          wrapperClassName="w-full"
          count={divide(row!.scheduled, row!.leads) * 100}
          breakdown={Object.entries<[key: string, value: { [key in `leads` | `scheduled`]: number }]>(
            row!.firstActivityTypes,
          )?.map(([key, value]) => ({
            name: key,
            count: divide(value[`scheduled`], value[`leads`]) * 100,
            colorHex: getActivityTypeColorMap(key as ActivityJson[`type`]),
          }))}
        >
          <div className="flex items-center gap-x-3">
            <ProgressBar progress={divide(row!.scheduled, row!.leads) * 100} />
            <span className="text-body-xs">
              <MetricCell value={divide(row!.scheduled, row!.leads) * 100} type="percentage" />
            </span>
          </div>
        </Stats>
      ),
    },
    enableHiding: true,
    footer: ({ table }) => (
      <MetricCell
        value={
          divide(
            table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original?.scheduled ?? 0), 0),
            table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original?.leads ?? 0), 0),
          ) * 100
        }
        type="percentage"
      />
    ),
  },
  {
    enableSorting: true,
    accessorFn: (row) => row.callScheduleRate,
    header: `Call Schedule Rate`,
    id: `Call Scheduled Rate`,
    meta: {
      group: `Schedule Rate`,
      defaultVisible: true,
      sortKey: `callScheduleRate`,
      infoTooltip: {
        title: `Call Schedule Rate`,
        message: `Percentage of qualified leads that scheduled a first appointment through a phone call within the date range.`,
      },
      ControlledComponent: (row) => (
        <div className="flex items-center gap-x-3 px-2">
          <ProgressBar progress={row.callScheduleRate} />
          <span className="text-body-xs">
            <MetricCell value={row.callScheduleRate} type="percentage" />
          </span>
        </div>
      ),
    },
    enableHiding: true,
    footer: ({ table }) => (
      <MetricCell
        value={
          (table
            .getFilteredRowModel()
            .rows.reduce((total, row) => total + (row.original?.scheduledLeadsWithCalls ?? 0), 0) /
            table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + (row.original?.activeLeadsWithCalls ?? 0), 0)) *
          100
        }
        type="percentage"
      />
    ),
  },
  ...leadStatuses,
]

export const callTypeTitle = (type: string) =>
  type === `qualified-np`
    ? `Qualified NP Calls`
    : type === `scheduled-np`
      ? `Scheduled NP Calls`
      : `Unscheduled NP Calls`

export const staffFilterFromRow = (row: any) =>
  row.id !== row.bucket ? { staffId: row.id } : { staffName: row.bucket }

export const getScorecardStaffColumns = (
  fromDate: string,
  toDate: string,
  channel: string,
  defaultFilters?: PredefinedFilter,
  onCallOpen?: (staffBucket: string, staffId: string, callType: string) => void,
  callFilters?: { channel?: string; locationIds?: string[]; filters?: string },
): ColumnDef<any, any>[] => [
  {
    header: `Staff`,
    id: `Staff`,
    accessorFn: (row) => row.bucket,
    meta: {
      group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
    footer: `Total`,
  },
  ...staffMetricColumns({
    onCallClick: (row, type) => {
      onCallOpen?.(row.bucket, row.id, type)
      showModal(
        <CallsModal
          title={callTypeTitle(type)}
          subtitle={row.bucket}
          {...staffFilterFromRow(row)}
          type={type as any}
          fromDate={fromDate}
          toDate={toDate}
          channel={callFilters?.channel}
          locationIds={callFilters?.locationIds}
          filters={callFilters?.filters}
          onClose={closeModal}
        />,
        {
          hideTitle: true,
          removePadding: true,
          isFullScreen: true,
          useRadix: true,
        },
      )
    },
  }),
]

export const locationMonthlyColumns = (isSMC: boolean) => [
  {
    accessorFn: (row) => row.bucket,
    id: `Date`,
    header: `Date`,
    cell: (props) => moment.utc(props.getValue()).format(`MMMM YYYY`),
    footer: `Total`,
    meta: {
      group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
  },
  {
    accessorKey: `leads`,
    cell: (props) => <MetricCell value={props.row.original.leads} />,
    accessorFn: (row) => row.leads,
    header: `Leads`,
    id: `Leads`,
    meta: {
      group: `Totals`,
      defaultVisible: true,
    },
  },
  {
    accessorKey: `MQLs`,
    cell: (props) => <MetricCell value={props.row.original.MQLs} />,
    accessorFn: (row) => row.MQLs,
    header: `Qualified Leads`,
    id: `Qualified Leads`,
    meta: {
      group: `Totals`,
      defaultVisible: true,
    },
  },
  ...(isSMC
    ? [
        {
          accessorKey: `budget`,
          cell: (props) => <MetricCell value={props.row.original.budget} type="dollars" />,
          accessorFn: (row) => row.budget,
          header: `Budget`,
          id: `Budget`,
          meta: {
            group: `Financial`,
            defaultVisible: true,
          },
        },
        {
          accessorKey: `leadGoal`,
          cell: (props) => <MetricCell value={props.row.original.leadGoal} type="unitless" />,
          accessorFn: (row) => row.leadGoal,
          header: `Lead Goal`,
          id: `Lead Goal`,
          meta: {
            group: `General`,
            defaultVisible: false,
          },
        },
      ]
    : []),
  {
    accessorKey: `production`,
    cell: (props) => <MetricCell value={props.row.original.production} type="dollars" />,
    accessorFn: (row) => row.production,
    header: `Production`,
    id: `Production`,
    meta: {
      group: `Financial`,
      defaultVisible: true,
    },
  },
  {
    accessorKey: `scheduledProduction`,
    cell: (props) => <MetricCell value={props.row.original.scheduledProduction} type="dollars" />,
    accessorFn: (row) => row.scheduledProduction,
    header: `Scheduled Production`,
    id: `Scheduled Production`,
    meta: {
      group: `Financial`,
      defaultVisible: true,
    },
  },
  {
    accessorKey: `ROI`,
    cell: (props) => <MetricCell value={props.row.original.ROI} type="percentage" />,
    accessorFn: (row) => row.ROI,
    header: `ROI`,
    id: `ROI`,
    meta: {
      group: `Financial`,
      defaultVisible: true,
    },
  },
  {
    accessorKey: `appointments`,
    cell: (props) => <MetricCell value={props.row.original.appointments} />,
    accessorFn: (row) => row.appointments,
    header: `Appointments Scheduled`,
    id: `Appointments Scheduled`,
    meta: {
      group: `Totals`,
      defaultVisible: true,
    },
  },
  {
    accessorKey: `patients`,
    cell: (props) => <MetricCell value={props.row.original.patients} />,
    accessorFn: (row) => row.patients,
    header: `New Patients`,
    id: `New Patients`,
    meta: {
      group: `Totals`,
      defaultVisible: true,
    },
  },
  {
    accessorKey: `CPL`,
    cell: (props) => <MetricCell value={props.row.original.CPL} type="dollars" />,
    accessorFn: (row) => row.CPL,
    header: `CPL`,
    id: `CPL`,
    meta: {
      group: `Calculations`,
      defaultVisible: false,
    },
  },
  {
    accessorKey: `CPQL`,
    cell: (props) => <MetricCell value={props.row.original.CPQL} type="dollars" />,
    accessorFn: (row) => row.CPQL,
    header: `CPQL`,
    id: `CPQL`,
    meta: {
      group: `Calculations`,
      defaultVisible: false,
    },
  },
  {
    accessorKey: `CPA`,
    cell: (props) => <MetricCell value={props.row.original.CPA} type="dollars" />,
    accessorFn: (row) => row.CPA,
    header: `CPA`,
    id: `CPA`,
    meta: {
      group: `Calculations`,
      defaultVisible: false,
    },
  },
  {
    accessorKey: `CAC`,
    cell: (props) => <MetricCell value={props.row.original.CAC} type="dollars" />,
    accessorFn: (row) => row.CAC,
    header: `CAC`,
    id: `CAC`,
    meta: {
      group: `Calculations`,
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.scheduleRate,
    accessorKey: `scheduleRate`,
    cell: (props) => <MetricCell value={props.row.original.scheduleRate} type="percentage" />,
    header: `Scheduled Rate`,
    id: `Scheduled Rate`,
    meta: {
      group: `Calculations`,
      defaultVisible: true,
    },
  },
  {
    accessorKey: `leadsInProgress`,
    accessorFn: (row) => row.leadsInProgress,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `In Progress`,
    id: `In Progress Leads`,
    meta: {
      group: `Lead Status`,
      defaultVisible: true,
    },
  },
  {
    accessorKey: `leadsScheduled`,
    accessorFn: (row) => row.leadsScheduled,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `Scheduled Leads`,
    id: `Scheduled Leads`,
    meta: {
      group: `Lead Status`,
      defaultVisible: true,
    },
  },
  {
    accessorKey: `leadsDisqualified`,
    accessorFn: (row) => row.leadsDisqualified,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `Disqualified`,
    id: `Disqualified Leads`,
    meta: {
      group: `Totals`,
      defaultVisible: false,
    },
  },
  {
    accessorKey: `leadsNeedsAction`,
    accessorFn: (row) => row.leadsNeedsAction,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `Needs Action`,
    id: `Needs Action Leads`,
    meta: {
      group: `Lead Status`,
      defaultVisible: true,
    },
  },
  {
    accessorKey: `leadsClosedWon`,
    accessorFn: (row) => row.leadsClosedWon,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `Closed Won`,
    id: `Closed Won Leads`,
    meta: {
      group: `Lead Status`,
      defaultVisible: false,
    },
  },
  {
    accessorKey: `leadsClosedLost`,
    accessorFn: (row) => row.leadsClosedLost,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `Closed Lost`,
    id: `Closed Lost Leads`,
    meta: {
      group: `Lead Status`,
      defaultVisible: true,
    },
  },
  // {
  //   accessorFn: (row) => row.leadsWithMissedCall,
  //   id: `Missed Calls`,
  //   header: `Missed Calls`,
  //   cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
  //   footer: ({ table }) => (
  //     <MetricCell
  //       value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsWithMissedCall, 0)}
  //       type="unitless"
  //     />
  //   ),
  //   meta: {
  //     group: `Totals`,
  //     defaultVisible: false,
  //   },
  // },
]

export const staffMonthlyColumns = [
  {
    accessorFn: (row) => row.date,
    id: `Date`,
    header: `Date`,
    cell: (props) => formatDate({ value: props.getValue(), format: `MMMM YYYY` }),
    footer: `Total`,
    meta: {
      group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
  },
  {
    enableSorting: true,
    accessorFn: (row) => row.scheduled,
    header: `Scheduled`,
    id: `Scheduled`,
    meta: {
      group: `Schedule Rate`,
      defaultVisible: true,
      sortKey: `Scheduled`,
    },
  },
  {
    enableSorting: true,
    accessorFn: (row) => row.notScheduled,
    header: `Not Scheduled`,
    id: `Not Scheduled`,
    meta: {
      group: `Schedule Rate`,
      defaultVisible: true,
      sortKey: `Not Scheduled`,
    },
  },
  {
    enableSorting: true,
    accessorFn: (row) => row.leads,
    header: `Qualified Leads`,
    id: `Qualified Leads`,
    meta: {
      group: `Schedule Rate`,
      defaultVisible: true,
      sortKey: `leads`,
    },
    enableHiding: true,
  },
  {
    enableSorting: true,
    header: `Scheduled Rate`,
    id: `Scheduled Rate`,
    meta: {
      group: `Schedule Rate`,
      defaultVisible: true,
      sortKey: `scheduled`,
      ControlledComponent: (row) => {
        let percentage = (row!.scheduled / row!.leads) * 100
        return (
          <Stats
            title="Scheduled Rate"
            unitType="percentage"
            wrapperClassName="w-full"
            count={percentage}
            breakdown={Object.entries<[key: string, value: { [key in `leads` | `scheduled`]: number }]>(
              row!.firstActivityTypes,
            )?.map(([key, value]) => ({
              name: key,
              count: (value[`scheduled`] / value[`leads`]) * 100,
              colorHex: getActivityTypeColorMap(key as ActivityJson[`type`]),
            }))}
          >
            <div className="flex items-center gap-x-3">
              <ProgressBar progress={percentage} />
              <span className="text-body-xs">
                <MetricCell value={percentage} type="percentage" />
              </span>
            </div>
          </Stats>
        )
      },
    },
    enableHiding: true,
  },
  ...leadStatuses,
]
